import { FormEvent, useEffect, useRef } from 'react';
import { Col, ControlLabel, FormControl, FormGroup, HelpBlock, Row } from 'react-bootstrap';
import { IMedicalRecord, useMedicalRecordApi } from '@nkr/models/medical-record';
import { IBaseDataValidation, NewMedicalRecord } from './data-model';
import { Utility } from '@nkr/services/utility';
import useDebounce from '@nkr/utils/useDebounce';

interface IProps {
    centerId: number,
    email: string,
    medicalRecordId: number,
    validation: IBaseDataValidation,
    disabled: boolean,
    showMedicalRecord: boolean,
    onFieldChange: (name: string, value: any) => void
}

const wrapperStyle = {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ccc',
    borderRadius: 4,
    margin: '0 -15px 15px',
    padding: '10px 15px 0'
};

export const MedicalRecordLinkEmail = (props: IProps) => {
    const { debouncedValue, isValid } = useDebounce(props.email, 750, Utility.isValidEmail);

    const api = useMedicalRecordApi(props.centerId);
    const searchable = props.showMedicalRecord && isValid && debouncedValue != '';
    const response = api.useSearch(searchable ? { email: debouncedValue, oneOnly: true } : null);

    const medicalRecord: IMedicalRecord = response?.payload?.length > 0 ? response.payload[0] : null;
    const targetId = medicalRecord != null ? medicalRecord.id : NewMedicalRecord;

    //the email or the record it resolves to changed, so the checkbox has to be confirmed against the new one
    const linkedTarget = useRef({ email: props.email, id: targetId });

    useEffect(() => {
        if (linkedTarget.current.email == props.email && linkedTarget.current.id == targetId) {
            return;
        }

        linkedTarget.current = { email: props.email, id: targetId };

        if (props.medicalRecordId != 0) {
            props.onFieldChange('medicalRecordId', 0);
        }
    }, [props.email, targetId]);

    const handleEmailChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        props.onFieldChange('email', e.currentTarget.value);
    };

    const handleMedicalRecordChange = (e: FormEvent<HTMLInputElement>) => {
        props.onFieldChange('medicalRecordId', e.currentTarget.checked ? targetId : 0);
    };

    const email = (
        <Col xs={12} lg={6} lgOffset={0}>
            <FormGroup validationState={props.validation.email == false ? 'error' : null}>
                <ControlLabel>Email</ControlLabel>
                <FormControl
                    name='email'
                    disabled={props.disabled}
                    value={props.email}
                    onChange={handleEmailChange} />
                <HelpBlock>
                    <span style={{ color: 'red' }}>*</span>
                    &nbsp; Must be a valid, unique email to identify medical record.
                </HelpBlock>
            </FormGroup>
        </Col>
    );

    if (props.showMedicalRecord == false) {
        return (
            <Row>
                {email}
            </Row>
        );
    }

    const lookupPending = props.email != debouncedValue;

    return (
        <div>
            <Row>
                {email}

                <Col xs={12} lg={6} lgOffset={0}>
                    <FormGroup validationState={props.validation.medicalRecord == false ? 'error' : null}>
                        <ControlLabel>Medical Record <span style={{ color: 'red' }}>*</span></ControlLabel>
                        <div className='checkbox' style={{ marginTop: 0 }}>
                            <label>
                                <input
                                    type='checkbox'
                                    name='medicalRecordId'
                                    disabled={props.disabled || searchable == false || lookupPending}
                                    checked={props.medicalRecordId != 0}
                                    value={targetId}
                                    onChange={handleMedicalRecordChange} />
                                {medicalRecord != null ?
                                    <>Existing Record for: {medicalRecord.firstName} {medicalRecord.lastName}<br />
                                        DOB: {medicalRecord.dob}</>
                                    :
                                    <>Add a new Medical Record</>
                                }
                            </label>
                        </div>

                        {props.validation.medicalRecord == false && searchable && (
                            <HelpBlock>
                                {medicalRecord != null ?
                                    'Please confirm this is the correct medical record'
                                    :
                                    'Please confirm this is a new medical record'
                                }
                            </HelpBlock>
                        )}

                        {searchable == false && (
                            <HelpBlock>Enter a valid email address to look up the medical record</HelpBlock>
                        )}
                    </FormGroup>
                </Col>
            </Row>
        </div>
    );
}
